import { Container, Sprite, TilingSprite, type Texture } from 'pixi.js';

// Brass frame around a w x h rect, built from two reusable parts:
//  - `border`: a vertical strip, shaded ACROSS its width (a directional
//    gradient) and uniform along its length (so it tiles seamlessly
//    lengthwise). It is the LEFT edge as-is; the other edges are rotated /
//    mirrored copies so the gradient stays consistent all the way round:
//      left   = original
//      top    = rotate +90
//      right  = mirror of left (flip X)
//      bottom = mirror of top (rotate -90)
//  - `corner`: the top-right corner; mirrored on X/Y for the other three.
// Both parts scale by the SAME factor (thickness / border.width) so the
// corner's arm thickness matches the edge strip thickness automatically.
//
// The frame's outer edge aligns with (0,0)..(w,h); add it over a panel of the
// same size.

const DEFAULT_THICKNESS = 12;
// How far each edge runs UNDER the corner (fraction of the corner size). The
// corners are drawn on top, so this overlap hides the edge/corner seam. < 1 so
// the strip stops short of the corner's rounded outer tip (no nub poking out).
const EDGE_OVERLAP_FRAC = 0.7;

export type OrnateBorderOptions = {
  width: number;
  height: number;
  border: Texture;
  corner: Texture;
  // Edge strip thickness in design px (corner scales to match). Default 12.
  thickness?: number;
};

export class OrnateBorder extends Container {
  constructor(opts: OrnateBorderOptions) {
    super();
    const { width: w, height: h, border, corner } = opts;
    const thickness = opts.thickness ?? DEFAULT_THICKNESS;
    const sf = thickness / border.width; // border.width = strip thickness axis
    const cw = corner.width * sf;
    const ch = corner.height * sf;

    // Edges extend under the corners by EDGE_OVERLAP_FRAC so the joints have no
    // gap (corners are drawn on top to cover the overlap).
    const hExt = cw * EDGE_OVERLAP_FRAC;
    const vExt = ch * EDGE_OVERLAP_FRAC;
    const vLen = Math.max(0, h - 2 * ch + 2 * vExt); // left/right run length
    const hLen = Math.max(0, w - 2 * cw + 2 * hExt); // top/bottom run length

    // One tile across the thickness, tiled along the length. Gradient runs
    // across local x (the thickness).
    const strip = (len: number): TilingSprite => {
      const s = new TilingSprite({ texture: border, width: thickness, height: len });
      s.tileScale.set(sf);
      return s;
    };

    // LEFT - original orientation.
    const left = strip(vLen);
    left.position.set(0, ch - vExt);
    this.addChild(left);

    // RIGHT - mirror of left across the thickness (flip X, shift back into place).
    const right = strip(vLen);
    right.scale.x = -1;
    right.position.set(w, ch - vExt);
    this.addChild(right);

    // TOP - left strip rotated +90. Local rect (0,0)-(thickness,hLen) rotated
    // +90 spans screen x:[x0-hLen, x0], y:[y0, y0+thickness] (x0 = right end).
    const top = strip(hLen);
    top.rotation = Math.PI / 2;
    top.position.set(w - cw + hExt, 0);
    this.addChild(top);

    // BOTTOM - mirror of top: rotate -90 so the gradient faces the other way.
    // Rotated -90 spans screen x:[x0, x0+hLen], y:[y0-thickness, y0] (x0 = left
    // end, y0 = bottom).
    const bottom = strip(hLen);
    bottom.rotation = -Math.PI / 2;
    bottom.position.set(cw - hExt, h);
    this.addChild(bottom);

    // Corners last so they cover the edge ends. Mirror via scale sign.
    this.addChild(cornerSprite(corner, sf, w - cw / 2, ch / 2, 1, 1)); // TR
    this.addChild(cornerSprite(corner, sf, cw / 2, ch / 2, -1, 1)); // TL
    this.addChild(cornerSprite(corner, sf, w - cw / 2, h - ch / 2, 1, -1)); // BR
    this.addChild(cornerSprite(corner, sf, cw / 2, h - ch / 2, -1, -1)); // BL
  }
}

function cornerSprite(
  texture: Texture,
  sf: number,
  cx: number,
  cy: number,
  signX: 1 | -1,
  signY: 1 | -1,
): Sprite {
  const s = new Sprite(texture);
  s.anchor.set(0.5);
  s.scale.set(sf * signX, sf * signY);
  s.position.set(cx, cy);
  return s;
}
